import { useState, useEffect, useMemo, useCallback } from "react";
import { collection, doc, getDoc } from "firebase/firestore";
import { db } from "../../../../services/firebase";
import { COLLECTIONS } from "../../../../services/collections";
import { useTenant } from "../../../../hooks/useTenant";
import { toISODate } from "../../../../utils/date";

/**
 * Hook para carregar métricas do Dashboard Gerencial.
 * 
 * Lê de:
 *   metrics/sales_YYYY-MM-DD   → vendas, receita financeira
 *   metrics/sales_YYYY-MM      → resumo mensal de vendas
 *   metrics/members_YYYY-MM-DD → novos, renovações, retornos
 *   metrics/members_YYYY-MM    → resumo mensal de membros
 *   metrics/snapshot_YYYY-MM-DD → alunos ativos, inativos, leads
 */

function getDateParts(date) {
    const d = date instanceof Date ? date : new Date(date);
    const iso = toISODate(d);
    const [year, month, day] = iso.split("-");
    return { year, month, day };
}

export default function useManagerDashboard() {
    const { idTenant } = useTenant();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Métricas de vendas
    const [salesToday, setSalesToday] = useState(null);
    const [salesMonth, setSalesMonth] = useState(null);
    const [salesPrevMonth, setSalesPrevMonth] = useState(null);

    // Métricas de membros
    const [membersToday, setMembersToday] = useState(null);
    const [membersMonth, setMembersMonth] = useState(null);
    const [membersPrevMonth, setMembersPrevMonth] = useState(null);

    // Snapshot de alunos ativos
    const [snapshot, setSnapshot] = useState(null);
    const [snapshotPrev, setSnapshotPrev] = useState(null);

    // Summary de alunos por atividade
    const [activitySummary, setActivitySummary] = useState(null);

    const [occupancySummary, setOccupancySummary] = useState(null);

    // Summary financeiro Global (MRR)
    const [financialSummary, setFinancialSummary] = useState(null);

    const today = useMemo(() => toISODate(new Date()), []);
    
    // Calcula um mês exato atrás para ter "dados do mês passado no mesmo dia"
    const prevDate = useMemo(() => {
        const d = new Date();
        d.setMonth(d.getMonth() - 1);
        return d;
    }, []);
    const previousMonthToday = useMemo(() => toISODate(prevDate), [prevDate]);
    const { year: prevYear, month: prevMonth } = useMemo(() => getDateParts(prevDate), [prevDate]);

    const { year, month, day } = useMemo(() => getDateParts(new Date()), []);

    const [chartFinancials, setChartFinancials] = useState([]);
    const [renewalChartData, setRenewalChartData] = useState([]);
    const [occupancyChartData, setOccupancyChartData] = useState([]);
    const [growthChartData, setGrowthChartData] = useState([]);

    const loadMetrics = useCallback(async () => {
        if (!idTenant) return;
        setLoading(true);
        setError(null);

        try {
            const metricsRef = collection(db, "tenants", idTenant, COLLECTIONS.METRICS);

            // Preparar leitura dos últimos 12 meses (para gráfico de Receitas x Despesas, Ocupação e Crescimento)
            const last12MonthsIds = [];
            const last12MonthsOccupancyIds = [];
            const last12MonthsSnapshotIds = [];
            const last12MonthsPrevSnapshotIds = [];
            const last12MonthsLabels = [];
            const currentDateTmp = new Date();
            for (let i = 11; i >= 0; i--) {
                const d = new Date(currentDateTmp.getFullYear(), currentDateTmp.getMonth() - i, 1);
                const y = d.getFullYear().toString();
                const prevY = (d.getFullYear() - 1).toString();
                const m = (d.getMonth() + 1).toString().padStart(2, "0");
                
                last12MonthsIds.push(`sales_${y}-${m}`);
                last12MonthsOccupancyIds.push(`occupancy_${y}-${m}`);
                last12MonthsSnapshotIds.push(`snapshot_monthly_${y}-${m}`);
                last12MonthsPrevSnapshotIds.push(`snapshot_monthly_${prevY}-${m}`);
                
                last12MonthsLabels.push(d.toLocaleDateString('pt-BR', { month: 'short' }).toUpperCase().replace('.', ''));
            }

            // Preparar leitura dos 12 meses de renovações (Ano atual vs Ano anterior)
            const currentYearForRenewals = parseInt(year, 10);
            const prevYearForRenewals = currentYearForRenewals - 1;
            const allMonths = ["01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12"];
            const currYearMemIds = allMonths.map(m => `members_${currentYearForRenewals}-${m}`);
            const prevYearMemIds = allMonths.map(m => `members_${prevYearForRenewals}-${m}`);

            // Buscar tudo em paralelo
            const [
                saleDailySnap,
                saleMonthlySnap,
                salePrevMonthlySnap,
                memberDailySnap,
                memberMonthlySnap,
                memberPrevMonthlySnap,
                snapshotSnap,
                snapshotSnapPrev,
                twelveMonthsSnaps,
                twelveMonthsOccupancySnaps,
                currYearMemSnaps,
                prevYearMemSnaps,
                activityMembersSnap,
                occupancySummarySnap,
                growthSnaps,
                growthPrevSnaps,
                finSummarySnap
            ] = await Promise.all([
                getDoc(doc(metricsRef, `sales_${year}-${month}-${day}`)),
                getDoc(doc(metricsRef, `sales_${year}-${month}`)),
                getDoc(doc(metricsRef, `sales_${prevYear}-${prevMonth}`)),
                getDoc(doc(metricsRef, `members_${year}-${month}-${day}`)),
                getDoc(doc(metricsRef, `members_${year}-${month}`)),
                getDoc(doc(metricsRef, `members_${prevYear}-${prevMonth}`)),
                getDoc(doc(metricsRef, `snapshot_${today}`)),
                getDoc(doc(metricsRef, `snapshot_${previousMonthToday}`)),
                Promise.all(last12MonthsIds.map(id => getDoc(doc(metricsRef, id)))),
                Promise.all(last12MonthsOccupancyIds.map(id => getDoc(doc(metricsRef, id)))),
                Promise.all(currYearMemIds.map(id => getDoc(doc(metricsRef, id)))),
                Promise.all(prevYearMemIds.map(id => getDoc(doc(metricsRef, id)))),
                getDoc(doc(metricsRef, "activity_members")),
                getDoc(doc(metricsRef, "occupancy_summary")),
                Promise.all(last12MonthsSnapshotIds.map(id => getDoc(doc(metricsRef, id)))),
                Promise.all(last12MonthsPrevSnapshotIds.map(id => getDoc(doc(metricsRef, id)))),
                getDoc(doc(metricsRef, "financial_summary"))
            ]);

            setSalesToday(saleDailySnap.exists() ? saleDailySnap.data() : null);
            setSalesMonth(saleMonthlySnap.exists() ? saleMonthlySnap.data() : null);
            setSalesPrevMonth(salePrevMonthlySnap.exists() ? salePrevMonthlySnap.data() : null);

            setMembersToday(memberDailySnap.exists() ? memberDailySnap.data() : null);
            setMembersMonth(memberMonthlySnap.exists() ? memberMonthlySnap.data() : null);
            setMembersPrevMonth(memberPrevMonthlySnap.exists() ? memberPrevMonthlySnap.data() : null);

            setSnapshot(snapshotSnap.exists() ? snapshotSnap.data() : null);
            setSnapshotPrev(snapshotSnapPrev.exists() ? snapshotSnapPrev.data() : null);

            setFinancialSummary(finSummarySnap.exists() ? finSummarySnap.data() : null);
            
            // Montar array dos 12 meses focado em fluxo de caixa
            const builtChartData = twelveMonthsSnaps.map((snap, idx) => {
                const d = snap.exists() ? snap.data() : {};
                const income = d.incomeGross || 0;
                const expense = d.expenseGross || 0;
                return {
                    month: last12MonthsLabels[idx].substring(0, 3), // JAN, FEV...
                    income,
                    expense,
                    net: income - expense
                };
            });
            setChartFinancials(builtChartData);

            // Montar array de Renovações
            const monthLabelsMap = ["JAN.", "FEV.", "MAR.", "ABR.", "MAI.", "JUN.", "JUL.", "AGO.", "SET.", "OUT.", "NOV.", "DEZ."];
            const builtRenewalData = currYearMemSnaps.map((snap, idx) => {
                const cData = snap.exists() ? snap.data() : {};
                const pData = prevYearMemSnaps[idx].exists() ? prevYearMemSnaps[idx].data() : {};
                return {
                    month: monthLabelsMap[idx],
                    current: cData.renewals || 0,
                    previous: pData.renewals || 0
                };
            });
            setRenewalChartData(builtRenewalData);

            // Processar summary de alunos por atividade
            if (activityMembersSnap.exists()) {
                const actData = activityMembersSnap.data();
                setActivitySummary(actData.activities || {});
            } else {
                setActivitySummary({});
            }

            // Ocupacao Geral
            const occSnapMap = { totalSpots: 0, occupiedSpots: 0, activities: {} };
            if (occupancySummarySnap.exists()) {
                const s = occupancySummarySnap.data();
                occSnapMap.totalSpots = s.totalCapacity || 0;
                occSnapMap.occupiedSpots = s.totalEnrolled || 0;
                occSnapMap.activities = s.activities || {};
            }
            setOccupancySummary(occSnapMap);

            const builtOccupancyData = twelveMonthsOccupancySnaps.map((snap, idx) => {
                const d = snap.exists() ? snap.data() : { totalCapacity: 0, totalEnrolled: 0 };
                const cap = d.totalCapacity || 0;
                const enr = d.totalEnrolled || 0;
                const pct = cap > 0 ? Math.round((enr / cap) * 100) : 0;
                // Ex: "JAN/26"
                const label = last12MonthsLabels[idx].substring(0, 3) + '/' + last12MonthsIds[idx].substring(8,10);
                return { month: label, capacity: cap, enrolled: enr, pct };
            });
            setOccupancyChartData(builtOccupancyData);

            // Mapeamento GrowthChart (Alunos x Ticket)
            const builtGrowthData = growthSnaps.map((snap, idx) => {
                const cData = snap.exists() ? snap.data() : {};
                const pData = growthPrevSnaps[idx].exists() ? growthPrevSnaps[idx].data() : {};
                
                return {
                    month: last12MonthsLabels[idx].substring(0, 3), // JAN, FEV...
                    currentActive: cData.totalActiveMembers || 0,
                    prevActive: pData.totalActiveMembers || 0,
                    ticket: cData.ticketMedio || 0,
                    mrr: cData.currentMRR || 0
                };
            });
            setGrowthChartData(builtGrowthData);

        } catch (err) {
            console.error("[useManagerDashboard] Erro ao carregar métricas:", err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [idTenant, year, month, day, prevYear, prevMonth, today, previousMonthToday]);

    useEffect(() => {
        loadMetrics();
    }, [loadMetrics]);

    // Dados processados para os cards e graficos
    const dashboardData = useMemo(() => {
        const activeMembers = snapshot?.totalActiveMembers ?? 0;
        const previousActiveMembers = snapshotPrev?.totalActiveMembers ?? 0;

        // O Ticket Médio e MRR reais devem priorizar o financial_summary (que atualiza real-time em vendas)
        const currentMRR = financialSummary?.currentMRR ?? snapshot?.currentMRR ?? 0;
        
        // Divisor exato (exclui contratos com MRR R$0 como bolsistas)
        const payingMemberships = financialSummary?.totalPayingMemberships ?? snapshot?.totalPayingMemberships ?? 0;
        const refDivider = payingMemberships > 0 ? payingMemberships : 1;
        const ticketMedio = currentMRR > 0 ? (currentMRR / refDivider) : 0;

        // Injetar os valores puramente Real-Time na ponta (mês atual) do Gráfico de Crescimento
        const realTimeGrowthData = [...growthChartData];
        if (realTimeGrowthData.length > 0) {
            const lastIdx = realTimeGrowthData.length - 1;
            realTimeGrowthData[lastIdx] = {
                ...realTimeGrowthData[lastIdx],
                currentActive: activeMembers,
                ticket: ticketMedio,
                mrr: currentMRR
            };
        }

        return {
            financialChartData: chartFinancials,
            renewalChartData,
            growthChartData: realTimeGrowthData,
            
            // Ticket Médio recalculado em RT
            ticketMedio: ticketMedio,
            previousTicketMedio: snapshotPrev?.ticketMedio || 0,
            currentMRR: currentMRR,
            previousMRR: snapshotPrev?.currentMRR || 0,
            
            // Vendas hoje
            salesTodayCount: salesToday?.salesCount ?? 0,
            salesTodayGross: salesToday?.salesGross ?? 0,
            
            // Vendas do mês vs Anterior
            salesMonthCount: salesMonth?.salesCount ?? 0,
            previousSalesMonthCount: salesPrevMonth?.salesCount ?? 0,
            salesMonthGross: salesMonth?.salesGross ?? 0,
            previousSalesMonthGross: salesPrevMonth?.salesGross ?? 0,
            
            // Receitas do mês vs Anterior
            incomeMonth: salesMonth?.incomeGross ?? 0,
            previousIncomeMonth: salesPrevMonth?.incomeGross ?? 0,
            expenseMonth: salesMonth?.expenseGross ?? 0,
            previousExpenseMonth: salesPrevMonth?.expenseGross ?? 0,

            // Membros hoje
            newMembersToday: membersToday?.newMembers ?? 0,
            renewalsToday: membersToday?.renewals ?? 0,
            returnsToday: membersToday?.returns ?? 0,

            // Movimentação do mês vs Anterior
            newMembersMonth: membersMonth?.newMembers ?? 0,
            previousNewMembersMonth: membersPrevMonth?.newMembers ?? 0,
            renewalsMonth: membersMonth?.renewals ?? 0,
            previousRenewalsMonth: membersPrevMonth?.renewals ?? 0,
            returnsMonth: membersMonth?.returns ?? 0,
            previousReturnsMonth: membersPrevMonth?.returns ?? 0,
            activationsMonth: membersMonth?.activationsTotal ?? 0,
            previousActivationsMonth: membersPrevMonth?.activationsTotal ?? 0,
            
            // Cancelamentos e Evasão
            cancellationsMonth: membersMonth?.cancellations ?? 0,
            previousCancellationsMonth: membersPrevMonth?.cancellations ?? 0,
            churnRate: (activeMembers > 0) ? (((membersMonth?.cancellations || 0) / activeMembers) * 100) : 0,
            previousChurnRate: (previousActiveMembers > 0) ? (((membersPrevMonth?.cancellations || 0) / previousActiveMembers) * 100) : 0,

            // Ativos (Snapshot Atual x Passado)
            activeMembers,
            previousActiveMembers,
            inactiveMembers: snapshot?.totalInactiveMembers ?? 0,
            previousInactiveMembers: snapshotPrev?.totalInactiveMembers ?? 0,
            leads: snapshot?.totalLeads ?? 0,
            previousLeads: snapshotPrev?.totalLeads ?? 0,
            activeMemberships: snapshot?.totalActiveMemberships ?? 0,

            // Alunos por atividade (para o gráfico donut)
            activityChartData: activitySummary
                ? Object.values(activitySummary)
                    .map(a => ({
                        name: a.name || "Sem nome",
                        color: a.color || "#3b82f6",
                        count: a.count || 0
                    }))
                    .filter(a => a.count > 0)
                    .sort((a, b) => b.count - a.count)
                : [],

            // Ocupação Total
            occupancy: {
                totalSpots: occupancySummary?.totalSpots || 0,
                occupiedSpots: occupancySummary?.occupiedSpots || 0,
                activities: occupancySummary?.activities || {},
                history: occupancyChartData
            }
        };
    }, [salesToday, salesMonth, salesPrevMonth, membersToday, membersMonth, membersPrevMonth, snapshot, snapshotPrev, chartFinancials, renewalChartData, activitySummary, occupancySummary, occupancyChartData, growthChartData, financialSummary]);

    return {
        loading,
        error,
        data: dashboardData,
        refresh: loadMetrics,
    };
}
